import React from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { collectAllNodeIds } from "services/fde/tciGovernanceService";

function resolveCompanionLabels(companionNodeIds = [], graph = []) {
  const labelById = {};
  const walk = (nodes) => {
    (nodes || []).forEach((node) => {
      if (node?.nodeId) labelById[node.nodeId] = node.label || node.nodeId;
      if (node?.children) walk(node.children);
      if (node?.child) walk([node.child]);
      if (node?.branches) {
        node.branches.forEach((branch) => {
          if (branch?.child) walk([branch.child]);
        });
      }
    });
  };
  walk(graph);

  return companionNodeIds.map((id) => ({
    id,
    label: labelById[id] ? `${labelById[id]} (${id})` : id,
    missing: !labelById[id],
  }));
}

const PromptGraphSidePanel = ({ node, graph, onClose }) => {
  if (!node) return null;

  const nodeType = node.nodeType;

  return (
    <Paper
      variant="outlined"
      sx={{
        width: 300,
        flexShrink: 0,
        p: 2,
        maxHeight: "100%",
        overflow: "auto",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle1">{node.label}</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close panel">
          <CloseRounded fontSize="small" />
        </IconButton>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {node.description}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {nodeType === "script" && (
        <Stack spacing={1}>
          <Typography variant="body2">
            <strong>queryId:</strong>{" "}
            <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>
              {node.queryId || "—"}
            </Typography>
          </Typography>
          <Typography variant="body2">
            <strong>scriptKey:</strong>{" "}
            <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>
              {node.scriptKey || "—"}
            </Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Static — no LLM inference
          </Typography>
        </Stack>
      )}

      {nodeType === "stream" && (
        <Stack spacing={1}>
          <Typography variant="body2">
            <strong>transport:</strong> {node.transport}
          </Typography>
          <Typography variant="body2">
            <strong>inputModality:</strong> {node.inputModality}
          </Typography>
          <Typography variant="body2">
            <strong>outputModality:</strong> {node.outputModality}
          </Typography>
          <Typography variant="body2">
            <strong>sessionInitCallable:</strong> {node.sessionInitCallable}
          </Typography>
          <Typography variant="body2">companionNodeIds:</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {resolveCompanionLabels(node.companionNodeIds, graph).map((row) => (
              <Chip
                key={row.id}
                size="small"
                label={row.label}
                color={row.missing ? "warning" : "default"}
                variant="outlined"
              />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Persistent bidirectional session — not a discrete LLM call
          </Typography>
        </Stack>
      )}

      {nodeType === "retrieval" && (
        <Stack spacing={1}>
          <Typography variant="body2">
            <strong>retrievalType:</strong> {node.retrievalType}
          </Typography>
          <Typography variant="body2">
            <strong>callableOrService:</strong> {node.callableOrService || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>outputTarget:</strong> {node.outputTarget || "—"}
          </Typography>
        </Stack>
      )}

      {nodeType === "conditional" && (
        <Stack spacing={1}>
          <Typography variant="body2">
            <strong>conditionType:</strong> {node.conditionType}
          </Typography>
          <Typography variant="body2">{node.conditionDescription}</Typography>
          <Typography variant="body2">Branches:</Typography>
          {(node.branches || []).map((branch) => (
            <Chip key={branch.label} size="small" label={branch.label} variant="outlined" />
          ))}
          <Typography variant="caption" color="text.secondary">
            Execution logic lives in service layer — this node documents the branching behavior
          </Typography>
        </Stack>
      )}
    </Paper>
  );
};

export { resolveCompanionLabels, collectAllNodeIds };
export default PromptGraphSidePanel;
